import {
  CssBaseline,
  List,
  styled,
  Toolbar,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { MainListItems, SecondaryListItems, RecentEventsListItems } from '../components/ListItems';
import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ReactComponent as DrawerArrowOpenIcon } from '../assets/img/drawer-arrow-open.svg';
import { ReactComponent as DrawerArrowCloseIcon } from '../assets/img/drawer-arrow-close.svg';
import ProfileMenu from '../components/common/ProfileMenu';

const drawerWidth: number = 280;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
    borderRight: '1px solid rgba(229, 231, 235, 0.8)',
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.04)',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

export default function DashboardTopNavbar() {
  const routeIndex = useMemo(() => {
    const index = new Map<string, number>();
    index.set('', 0);
    index.set('recent-events', 1);
    index.set('turbine-interaction', 2);
    index.set('statistics', 3);
    return index;
  }, []);
  
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const [currentRouteIndex, setCurrentRouteIndex] = React.useState<
    number | undefined
  >(0);
  const [showStatisticsSubHeader, setShowStatisticsSubHeader] =
    React.useState(false);
  const [showRecentEventsSubHeader, setShowRecentEventsSubHeader] =
    React.useState(false);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    setCurrentRouteIndex(routeIndex.get(location.pathname.slice(1).toString()));
    if (location.pathname === '/statistics') {
      setShowStatisticsSubHeader(true);
      setShowRecentEventsSubHeader(false);
    } else if (location.pathname === '/recent-events') {
      setShowRecentEventsSubHeader(true);
      setShowStatisticsSubHeader(false);
    } else {
      setShowStatisticsSubHeader(false);
      setShowRecentEventsSubHeader(false);
    }
  }, [location.pathname, routeIndex]);

  return (
    <>
      <CssBaseline />
      {/* Toggle Button */}
      <IconButton
        onClick={toggleDrawer}
        sx={{
          zIndex: 102,
          position: 'fixed',
          top: '50px',
          left: open ? '260px' : '50px',
          transition: 'all 0.3s ease-in-out',
          backgroundColor: 'white',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            transform: 'scale(1.05)',
          },
        }}
      >
        {open ? <DrawerArrowCloseIcon /> : <DrawerArrowOpenIcon />}
      </IconButton>

      {/* Profile Menu */}
      <Box sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        height: '64px',
        zIndex: 1100
      }}>
        <ProfileMenu />
      </Box>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          height: '100vh',
          minWidth: '280px',
          position: 'fixed',
          zIndex: 101,
          mt: '64px',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
            minHeight: '32px',
          }}
        />



        {/* Navigation Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 32px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(107, 114, 128, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(107, 114, 128, 0.4)',
            },
          }}
        >
          <List component="nav">
            <MainListItems currentRouteIndex={currentRouteIndex} isCollapsed={!open} />
          </List>

          {showStatisticsSubHeader && open && (
            <>
              <Divider sx={{ margin: '16px 24px', opacity: 0.6 }} />
              <SecondaryListItems />
            </>
          )}

          {showRecentEventsSubHeader && open && (
            <>
              <Divider sx={{ margin: '16px 24px', opacity: 0.6 }} />
              <RecentEventsListItems />
            </>
          )}
        </Box>
      </Drawer>

      <Outlet />
    </>
  );
}
